/**
 * The preview — the point of the slice.
 *
 * **Nothing here compiles anything.** The HTML rendered below was produced by
 * the API, by the same function that produced the template's stored
 * `htmlCompiled` and the same one slice 5 will send. There is deliberately no
 * client-side "quick preview" to fall back on when the request is in flight or
 * has failed: a second renderer is a preview that eventually disagrees with the
 * email, and it disagrees at the moment the campaign has already gone out. When
 * the API has not answered, this screen shows the last HTML it *did* answer with
 * and says that is what it is doing.
 *
 * **The frame cannot run script and cannot reach out.** `sandbox` is set to the
 * empty string, which is every restriction switched on at once — see the comment
 * on the `<iframe>` for what each token would have allowed and why none of them
 * is here. Block text is escaped at render — a formatted paragraph's runs too,
 * each run's text escaped and wrapped only in tags the renderer chose — so the
 * HTML should not contain script in the first place; the frame is what makes
 * that guarantee hold even for HTML this slice did not produce.
 *
 * **The fidelity claim is checked, not asserted.** With sample substitution off,
 * the preview is the compile of the document with nothing done to it afterwards
 * — so when the document on screen is the saved one, it must be byte-identical
 * to the template's stored `htmlCompiled`. This component compares the two
 * strings and reports what it found. That is done-when #2 of slice 4, on the
 * screen, rather than in a script nobody runs twice.
 *
 * **Slice 11 narrowed what that comparison proves, and did not weaken it.** The
 * comparison is unchanged and is still exact: these bytes are the bytes Wend
 * hands to Amazon. What it never covered, and what nobody had to think about
 * until now, is what happens *after* the handover. With open tracking on, SES
 * injects a 1×1 pixel; with click tracking on, SES rewrites every `href` so a
 * click passes through a redirect first. Both are server-side, so Wend's bytes
 * are identical either way and the comparison passes exactly as before — which
 * is precisely why the claim on it had to be reworded rather than left to be
 * read as "this is what will land in the inbox".
 *
 * **The links are the honest gap, and it stays a gap.** With click tracking on,
 * the address a recipient hovers is not the address in the frame below, and Wend
 * cannot show the real one: the rewritten URL does not exist until SES makes it,
 * and it encodes the message. Rendering a plausible `https://links.example.in/CL0/…`
 * beside each link would make this panel look complete and would be wrong in
 * every character after the hostname — a preview that invents what a recipient
 * sees is a worse failure than one that admits what it cannot show. So
 * `previewGap` says it in words, under the comparison, and the frame keeps
 * showing the links the author actually wrote.
 *
 * ## Slice 17 — the promise, restated in a quarter of the words
 *
 * **The preview promise as it now stands is the whole reason this panel exists,
 * and it is not allowed to drift back to the simpler, false version.** What you
 * preview is what Wend sends; with click tracking on, SES rewrites every link
 * *after* the handover, so the frame shows the originals and the notice under
 * the comparison says so. That is two facts, not one, and cutting either half
 * produces a sentence that has already shipped here as a lie — the strong
 * headline is therefore still gated on `makesNoRequestOnOpen`, and `previewGap`
 * still renders whenever there is a gap to admit.
 *
 * What did change is everything around them. The three "not compared" states —
 * substitution on, unsaved changes, never compiled — each argued their own case
 * in three lines; they now lead with the words **Not compared** and say why in
 * one clause, because a person reads them to find out whether the comparison
 * ran, not to be persuaded that the rule is sound. The `Fidelity` and `Note`
 * headers below keep the reasoning.
 *
 * `PREVIEW_SANDBOX_NOTE` and `PREVIEW_IMAGES_NOTE` were one paragraph and are
 * now two things with two fates. The images note is kind 2 — without it, an
 * author whose images render as alt text concludes the upload is broken — so it
 * stays on the screen, and still only when the document has an image block. The
 * sandbox note answers a question nobody asks until they ask it, and folds into
 * `MoreDetail`, which keeps it in the DOM for find-in-page.
 *
 * **The placeholder is the frame's height.** `h-[70vh] min-h-[24rem]` is
 * repeated on the pre-compile box so the first answer does not shove the
 * approval callouts and the variable list down the page under a cursor already
 * moving towards one of them.
 *
 * ## Slice 56 — a transparent email must not look white, and a wide one wide
 *
 * The frame is still `<iframe sandbox="" srcDoc>` and nothing else on this
 * screen renders the compiled HTML. What changed is what is drawn **behind**
 * it. With `background: 'transparent'` the email declares no background at all,
 * so what a reader sees is their client's — and a white frame would show white,
 * which is precisely the thing the person just switched off. So in that mode the
 * frame itself is transparent and sits on a checker, the convention every image
 * editor uses for "nothing here". For the frame to be see-through at all it has
 * to share the embedded document's colour scheme: this app declares
 * `color-scheme: dark`, the email declares none, and a browser paints an opaque
 * canvas behind an iframe whose scheme differs from its document's. The frame
 * is therefore set to `color-scheme: light` in that mode. In the white mode
 * nothing about the frame changes.
 *
 * `bodyWidth: 'full'` widens the Desktop frame from 640px to the panel's whole
 * width. A 640px frame was a fair stand-in for a reading pane when every email
 * was a 600px column; for an email that spans the reader's width it would show
 * a column 40px wider than before and nothing of what the setting does. Mobile
 * stays 375px in both modes, where the two already look alike.
 */
import {
  SAMPLE_VALUES_NOTICE,
  type TemplateBackground,
  type TemplateBodyWidth,
  type TemplatePreviewResponse,
} from '@wend/shared';
import { Callout, Checkbox, Kicker, SegmentedToggle, Spinner, Tag, cn } from '@wend/ui';
import { useState, type ReactNode } from 'react';

import { FormAlert } from '../../components/FormAlert';
import { errorMessage, isApiError } from '../../lib/api/errors';
import { formatCount } from '../../lib/format';
import {
  makesNoRequestOnOpen,
  previewGap,
  sesModifiesMessage,
  type TrackingKnowledge,
} from '../../lib/recipient-experience';
import {
  PREVIEW_IMAGES_NOTE,
  PREVIEW_SANDBOX_NOTE,
  TEMPLATE_APPROVAL_BLOCKER_TITLE,
} from '../../lib/template-display';
import { MoreDetail } from './MoreDetail';
import { RecipientTrackingNotice } from './RecipientTrackingNotice';

/**
 * The width every email template has settled on, and a small phone.
 *
 * Slice 66 moved the switch between them onto the shared `SegmentedToggle` —
 * one bordered group of two `aria-pressed` buttons, the Blueprint's toggle —
 * from two free-standing Buttons that read as two actions. It toggles a value,
 * so it is not Tabs, and it drops the monitor and phone glyphs: the toggle
 * carries words only, and the words already say which is which.
 */
const WIDTHS = {
  desktop: { label: 'Desktop', width: '640px' },
  mobile: { label: 'Mobile', width: '375px' },
} as const;

type WidthKey = keyof typeof WIDTHS;

const WIDTH_KEYS = Object.keys(WIDTHS) as WidthKey[];

/**
 * "Nothing here", drawn the way an image editor draws it — see the header.
 * Two light greys, because what shows through in most inboxes is light, and a
 * dark checker would suggest the email turns dark.
 */
const TRANSPARENCY_CHECKER = {
  backgroundColor: '#f4f4f5',
  backgroundImage:
    'linear-gradient(45deg, #dcdce0 25%, transparent 25%, transparent 75%, #dcdce0 75%), linear-gradient(45deg, #dcdce0 25%, transparent 25%, transparent 75%, #dcdce0 75%)',
  backgroundPosition: '0 0, 8px 8px',
  backgroundSize: '16px 16px',
} as const;

export interface TemplatePreviewProps {
  /** The last answer from the API, or null before the first one arrives. */
  preview: TemplatePreviewResponse | null;
  /** The stored compiled HTML, for the byte-for-byte comparison. */
  storedHtml: string | null;
  /** True when the document on screen differs from the saved one. */
  dirty: boolean;
  substitute: boolean;
  onSubstituteChange: (substitute: boolean) => void;
  /** A preview request is in flight. */
  loading: boolean;
  /** The request failed — usually because the document is not valid yet. */
  error: unknown;
  /** The document has at least one image block, which the frame cannot fetch. */
  hasImages: boolean;
  /**
   * True when this deployment links to files **and** this template has one —
   * so the message a recipient receives carries an "Attachments" section that
   * `html_compiled`, and therefore this preview, does not.
   *
   * It exists because of what this panel is allowed to claim. "This is exactly
   * the email that will be sent" was true whenever Amazon added nothing; since
   * slice 23 **Wend** adds something too, at send time, and a person who read
   * the strong headline and saw no Attachments section would reasonably conclude
   * the links were not going out. The comparison itself is untouched: the bytes
   * being compared are still the stored ones.
   */
  addsAttachmentLinks: boolean;
  /**
   * What this workspace's tracking is set to, read once by the editor.
   *
   * It changes no HTML and no comparison — it decides only what this panel is
   * entitled to claim about the message a recipient receives, which is not the
   * same thing as the message Wend sends once tracking is on.
   */
  tracking: TrackingKnowledge;
  /**
   * The width setting the frame's HTML was compiled for (slice 56). 'full'
   * widens the Desktop frame to the panel — see the header.
   */
  bodyWidth: TemplateBodyWidth;
  /**
   * The background setting the frame's HTML was compiled for (slice 56).
   * 'transparent' draws a checker behind a see-through frame, so a transparent
   * email does not read as a white one.
   */
  background: TemplateBackground;
}

export function TemplatePreview({
  preview,
  storedHtml,
  dirty,
  substitute,
  onSubstituteChange,
  loading,
  error,
  hasImages,
  addsAttachmentLinks,
  tracking,
  bodyWidth,
  background,
}: TemplatePreviewProps) {
  const [width, setWidth] = useState<WidthKey>('desktop');
  const gap = previewGap(tracking);
  const frameWidth = width === 'desktop' && bodyWidth === 'full' ? '100%' : WIDTHS[width].width;
  const transparent = background === 'transparent';

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <Checkbox
          checked={substitute}
          onCheckedChange={onSubstituteChange}
          label="Show sample values"
          description="Replaces every {{ variable }} with an obviously fictional value."
        />

        <SegmentedToggle
          className="ml-auto"
          label="Preview width"
          value={width}
          options={WIDTH_KEYS.map((key) => ({ value: key, label: WIDTHS[key].label }))}
          onValueChange={(next) => {
            const key = WIDTH_KEYS.find((candidate) => candidate === next);
            if (key !== undefined) setWidth(key);
          }}
        />

        {loading && <Spinner size="sm" label="Compiling the preview" />}
      </div>

      {/*
        `FormAlert`, not `ErrorState`, and the distinction is load-bearing here
        rather than stylistic. This is almost always the compile endpoint's
        verdict on the document being typed — an unparseable variable, a
        `javascript:` link — which is a verdict on a submission, not a screen
        that could not load. There is no retry button for the same reason: the
        next keystroke re-asks, so a "try again" would re-send the request that
        was just refused for a reason that has not changed. The request id
        travels with it, because an unexpected 500 lands in this same branch.
      */}
      {error !== null && (
        <FormAlert
          tone="error"
          {...(isApiError(error) && error.requestId !== undefined
            ? { requestId: error.requestId }
            : {})}
        >
          {errorMessage(error)}{' '}
          {preview === null
            ? 'Nothing has compiled yet, so there is nothing to show.'
            : 'The preview below is the last version that compiled — not what is on screen now.'}
        </FormAlert>
      )}

      {preview === null ? (
        /*
          The placeholder is the frame's own height, not a shorter box that
          grows into it. The frame below is `h-[70vh] min-h-[24rem]`, and those
          exact values are repeated here so that the first compile lands without
          the approval callouts and the variable list jumping down the page
          under a cursor that was already on its way to one of them.
        */
        <div className="grid h-[70vh] min-h-[24rem] place-items-center rounded-base border border-line bg-white/[0.02] px-6 py-12 text-center">
          <p className="text-[0.92rem] text-muted" role="status">
            {loading ? 'Compiling…' : 'Nothing has been compiled yet.'}
          </p>
        </div>
      ) : (
        <>
          <InboxLine preview={preview} />

          <div className="overflow-x-auto rounded-base border border-line bg-surface-2/40 p-4">
            <div
              className="mx-auto rounded-sm"
              style={{ width: frameWidth, maxWidth: '100%', ...(transparent ? TRANSPARENCY_CHECKER : {}) }}
            >
              {/*
                The frame the compiled email renders in.

                `sandbox=""` is deliberate and is the whole guarantee: an empty
                value applies **every** restriction. Nothing is allowed in, and
                each omission is doing a job —

                  allow-scripts        would let the email run JavaScript;
                  allow-same-origin    would give it this app's origin, and with
                                       it the session, localStorage and the DOM
                                       of the page around it. It is also the one
                                       that must never be combined with
                                       allow-scripts, because together they let
                                       a frame remove its own sandbox;
                  allow-forms          would let it post somewhere;
                  allow-popups         would let it open a window;
                  allow-top-navigation would let it navigate the app away;
                  allow-modals         would let it call alert();
                  allow-downloads      would let it start a download.

                A preview needs none of them. It is a picture of an email, and
                the links in it are for reading rather than following — clicking
                one does nothing here, which is the correct outcome for a frame
                that must not be able to navigate anything.

                `srcdoc` rather than a blob URL so the HTML never becomes a
                fetchable document with an address of its own, and
                `referrerPolicy="no-referrer"` so nothing it does attempt carries
                where it came from.

                Slice 56 changed only the frame's own background, and only for
                a transparent email: `bg-white` becomes `bg-transparent` and the
                frame takes the light colour scheme, so the checker behind it
                shows through. The header says why the scheme is needed.
              */}
              <iframe
                title="Email preview"
                srcDoc={preview.html}
                sandbox=""
                referrerPolicy="no-referrer"
                className={cn(
                  'h-[70vh] min-h-[24rem] w-full rounded-sm border-0',
                  transparent ? 'bg-transparent' : 'bg-white',
                )}
                style={transparent ? { colorScheme: 'light' } : undefined}
              />
            </div>
          </div>

          {/*
            Two notes with two different jobs, so they are no longer one
            paragraph. `PREVIEW_IMAGES_NOTE` is kind 2 and stays on the screen —
            without it, an author whose images render as alt text concludes the
            upload is broken, and it is shown only when the document actually has
            an image block. `PREVIEW_SANDBOX_NOTE` answers a question nobody asks
            until they ask it, and folds.
          */}
          {hasImages && (
            <p className="max-w-prose text-[0.8rem] leading-relaxed text-muted">
              {PREVIEW_IMAGES_NOTE}
            </p>
          )}

          <MoreDetail summary="How this frame is rendered">
            <p>{PREVIEW_SANDBOX_NOTE}</p>
          </MoreDetail>

          <Fidelity
            preview={preview}
            storedHtml={storedHtml}
            dirty={dirty}
            tracking={tracking}
            addsAttachmentLinks={addsAttachmentLinks}
          />

          {/*
            Under the comparison, never instead of it. The comparison answers
            "did two code paths produce the same email"; this answers "is that
            email what a recipient receives", and with tracking on the honest
            answer to the second is "not quite, and here is the part Wend cannot
            show you".
          */}
          {gap !== null && <RecipientTrackingNotice notice={gap} />}

          <ApprovalIssues preview={preview} />
          <SampleValues preview={preview} />
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The inbox line                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Subject and preheader, as the API rendered them — substituted the same way the
 * body was.
 *
 * They belong above the frame because that is where they are in an inbox, and
 * because the subject is the field approval refuses a template for not having.
 */
function InboxLine({ preview }: { preview: TemplatePreviewResponse }) {
  return (
    <div className="rounded-base border border-line bg-white/[0.02] px-4 py-3">
      <p className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-muted">Subject</p>
      {preview.subject === null || preview.subject.trim() === '' ? (
        <p className="text-[0.95rem] text-warn">No subject line yet</p>
      ) : (
        <p className="text-[0.95rem] text-text">{preview.subject}</p>
      )}

      {preview.preheader !== null && preview.preheader.trim() !== '' && (
        <p className="mt-1 truncate text-[0.85rem] text-muted" title={preview.preheader}>
          {preview.preheader}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Is this the email that will be sent?                                        */
/* -------------------------------------------------------------------------- */

/**
 * The byte-for-byte comparison, done here rather than claimed.
 *
 * It only means anything under two conditions, and both are stated on screen
 * rather than assumed: sample substitution must be off (it rewrites the HTML
 * after the compile, on purpose), and the document on screen must be the saved
 * one (otherwise the preview is of something that was never stored, and a
 * difference is the expected answer).
 *
 * When both hold and the strings differ, that is a real problem — two code paths
 * producing two emails — and it says so in red rather than quietly showing a
 * preview nobody can trust.
 *
 * **The comparison is unchanged by slice 11. Its headline is not.** It used to
 * read "This is exactly the email that will be sent", which was a fair reading
 * of a true fact until SES started adding to the message after Wend handed it
 * over. The fact the comparison establishes — *these bytes are the bytes Wend
 * sends* — is still exactly true for every workspace, so the strong headline is
 * kept for a workspace that adds nothing, and a workspace whose mail SES
 * modifies gets a headline that stops one word short of a claim this panel
 * cannot make. What SES adds is spelled out in the notice below, from
 * `previewGap`, rather than crammed into a title.
 */
function Fidelity({
  preview,
  storedHtml,
  dirty,
  tracking,
  addsAttachmentLinks,
}: {
  preview: TemplatePreviewResponse;
  storedHtml: string | null;
  dirty: boolean;
  tracking: TrackingKnowledge;
  /** See `TemplatePreviewProps.addsAttachmentLinks`. */
  addsAttachmentLinks: boolean;
}) {
  if (preview.substituted) {
    return (
      <Note>
        Not compared — sample values are substituted after the compile. Turn them off to check this
        preview against the stored email byte for byte.
      </Note>
    );
  }

  if (dirty) {
    return (
      <Note>
        Not compared — there are unsaved changes, so this document was never stored. Save, and the
        two are checked against each other.
      </Note>
    );
  }

  if (storedHtml === null) {
    return (
      <Note>
        Not compared — this template has never been compiled and stored, so there is nothing to
        compare against.
      </Note>
    );
  }

  const identical = preview.html === storedHtml;

  // Three states, not two. `untouched` earns the strong headline slice 4 wrote;
  // `modified` earns the sentence about Amazon; and while the settings are still
  // being read neither is claimed, so the headline falls back to the narrower
  // one — which is true for every workspace, always.
  // `untouched` earns the strong headline only if nothing is added to the body
  // afterwards — by Amazon, and since slice 23 by Wend itself.
  const untouched = makesNoRequestOnOpen(tracking) && !addsAttachmentLinks;
  const modified = sesModifiesMessage(tracking);

  return identical ? (
    <Callout
      tone="good"
      title={
        untouched
          ? 'This is exactly the email that will be sent'
          : 'These are exactly the bytes Wend will send'
      }
    >
      Byte for byte, {formatCount(preview.html.length)} characters — compared here rather than
      eyeballed.
      {addsAttachmentLinks &&
        ' Wend adds one thing when it sends: a link to each of this template’s files, under the footer. The files are on the edit tab.'}
      {modified && ' Amazon changes the message after Wend hands it over; see the note below.'}
    </Callout>
  ) : (
    <Callout tone="bad" title="The preview and the stored email are not the same bytes">
      These two should be identical and are not — the preview is{' '}
      {formatCount(preview.html.length)} characters and the stored email is{' '}
      {formatCount(storedHtml.length)}. Do not send this template until that is understood.
    </Callout>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">{children}</p>;
}

/* -------------------------------------------------------------------------- */
/* What still stops this being approved                                        */
/* -------------------------------------------------------------------------- */

/**
 * The approval blockers, from the API.
 *
 * `templateApprovalIssues` is the function that decides whether the status
 * endpoint allows the transition, and the preview response carries its verdict —
 * so what is listed here is what would actually refuse the approval, not this
 * screen's opinion about what looks finished.
 */
function IssueList({ issues }: { issues: TemplatePreviewResponse['approvalIssues'] }) {
  return (
    <ul className="flex list-none flex-col gap-2.5">
      {issues.map((issue) => (
        <li key={issue.message}>
          <p className="font-mono text-[0.66rem] uppercase tracking-[0.12em] text-muted">
            {TEMPLATE_APPROVAL_BLOCKER_TITLE[issue.blocker]}
          </p>
          <p className="text-[0.88rem] leading-snug">{issue.message}</p>
        </li>
      ))}
    </ul>
  );
}

function ApprovalIssues({ preview }: { preview: TemplatePreviewResponse }) {
  // Split on the API's own verdict rather than on this screen's opinion. A
  // blocker is what the status endpoint would actually refuse; advice is shown
  // and never refused, so presenting the two the same way would be this screen
  // inventing a rule the server does not have.
  const blockers = preview.approvalIssues.filter((issue) => issue.severity === 'blocker');
  const advice = preview.approvalIssues.filter((issue) => issue.severity === 'advice');

  return (
    <div className="flex flex-col gap-3">
      {blockers.length === 0 ? (
        <Callout tone="good" title="Ready to approve">
          Nothing is stopping this template. Approving makes it available to a campaign.
        </Callout>
      ) : (
        <Callout tone="warn" title="Not ready to approve yet">
          <IssueList issues={blockers} />
        </Callout>
      )}

      {advice.length > 0 && (
        <Callout tone="info" title="Worth knowing before you send">
          <IssueList issues={advice} />
        </Callout>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The samples                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The variables the document uses and what was put in their place.
 *
 * Labelled as samples in the API's own words (`SAMPLE_VALUES_NOTICE`), because
 * a preview showing a plausible real name is a preview somebody screenshots into
 * a deck — and a real customer in a deck is a data incident with a slide number.
 */
function SampleValues({ preview }: { preview: TemplatePreviewResponse }) {
  if (preview.variables.length === 0) {
    return <Note>No variables — every recipient receives the same text.</Note>;
  }

  return (
    <section className="rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
      <Kicker as="h3" rule>
        Variables in this template
      </Kicker>
      {/*
        `SAMPLE_VALUES_NOTICE` is the API's own wording and stays verbatim: it is
        what stops a plausible real-looking name in a screenshot being taken for
        a customer's. Everything around it is one clause now.
      */}
      <p className="mt-1.5 text-[0.85rem] leading-relaxed text-muted">
        {preview.substituted
          ? SAMPLE_VALUES_NOTICE
          : 'Sample values are off, so the tokens below appear exactly as written.'}{' '}
        Each is replaced per recipient at send time.
      </p>

      <dl className="mt-3 grid gap-x-6 sm:grid-cols-2">
        {preview.variables.map((variable) => (
          <div key={variable} className="flex flex-col gap-0.5 border-t border-line py-2.5">
            {/*
              Slice 66: the token is a brand Tag — the Blueprint's mono chip for
              "something Wend fills in" — at a readable size and in its own case.
              The Tag role is uppercase by default, and a variable's name is
              case-sensitive, so `normal-case` is not styling: `{{ FIRST_NAME }}`
              is a different variable from the one in the template.
            */}
            <dt>
              <Tag tone="brand" className="text-[0.74rem] normal-case tracking-normal">
                {`{{ ${variable} }}`}
              </Tag>
            </dt>
            <dd
              className={cn(
                'text-[0.85rem]',
                preview.substituted ? 'text-muted' : 'text-muted',
              )}
            >
              {preview.substituted
                ? (preview.sampleValues[variable] ?? '—')
                : 'not substituted'}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
